import { Directive, ElementRef, forwardRef, HostListener } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

type IonicFormElement = HTMLElement & {
  checked?: boolean;
  disabled?: boolean;
  value?: unknown;
};

@Directive({
  selector: `
    ion-checkbox[formControlName],
    ion-checkbox[formControl],
    ion-checkbox[ngModel],
    ion-input[formControlName],
    ion-input[formControl],
    ion-input[ngModel],
    ion-range[formControlName],
    ion-range[formControl],
    ion-range[ngModel],
    ion-select[formControlName],
    ion-select[formControl],
    ion-select[ngModel],
    ion-textarea[formControlName],
    ion-textarea[formControl],
    ion-textarea[ngModel],
    ion-toggle[formControlName],
    ion-toggle[formControl],
    ion-toggle[ngModel]
  `,
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IonicValueAccessorDirective),
      multi: true
    }
  ]
})
export class IonicValueAccessorDirective implements ControlValueAccessor {
  private onChange: (value: unknown) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor(private readonly elementRef: ElementRef<IonicFormElement>) {}

  writeValue(value: unknown): void {
    const element = this.elementRef.nativeElement;

    if (this.usesCheckedValue(element)) {
      element.checked = Boolean(value);
      return;
    }

    element.value = value ?? '';
  }

  registerOnChange(onChange: (value: unknown) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState(isDisabled: boolean): void {
    this.elementRef.nativeElement.disabled = isDisabled;
  }

  @HostListener('ionChange', ['$event'])
  handleIonChange(event: CustomEvent): void {
    this.onChange(this.valueFromEvent(event));
  }

  @HostListener('ionInput', ['$event'])
  handleIonInput(event: CustomEvent): void {
    if (this.usesCheckedValue(this.elementRef.nativeElement)) {
      return;
    }

    this.onChange(this.valueFromEvent(event));
  }

  @HostListener('ionBlur')
  handleIonBlur(): void {
    this.onTouched();
  }

  private valueFromEvent(event: CustomEvent): unknown {
    const element = this.elementRef.nativeElement;

    if (this.usesCheckedValue(element)) {
      return Boolean(event.detail?.checked ?? element.checked);
    }

    return event.detail?.value ?? element.value ?? '';
  }

  private usesCheckedValue(element: IonicFormElement): boolean {
    return ['ION-CHECKBOX', 'ION-TOGGLE'].includes(element.tagName);
  }
}
